import { useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  useGetConsignmentLinesQuery,
  CONSIGNMENT_STATUS,
  ConsignmentStatusCode,
  useAssignLineToPallet,
  useDeletePallet,
  buildConsignmentPallets
} from '@/entities/consignments';
import { getApiErrorMessage, usePermissions, useProjectSiteParams } from '@/shared/lib';

import { transformToHierarchicalRows } from './transform-pallet-data';
import { calculateSelectedLinesInfo } from '../lib/calculate-units';
import { checkPalletCapacity } from '../lib/check-capacity';
import type { LocalPallet } from './types';

export interface UsePalletViewReturn {
  // ─── Core Data ─────────────────────────────────────────────────────────
  tableData: ReturnType<typeof transformToHierarchicalRows>;
  pallets: LocalPallet[];
  isLoading: boolean;
  error: unknown;
  canEdit: boolean;

  // ─── Selection State ───────────────────────────────────────────────────
  selectedLineIds: string[];
  selectedLinesInfo: ReturnType<typeof calculateSelectedLinesInfo>;
  setSelectedLineIds: (ids: string[]) => void;

  // ─── UI State ──────────────────────────────────────────────────────────
  expandedRows: Set<string>;
  toggleExpanded: (palletId: string) => void;

  // ─── Actions ───────────────────────────────────────────────────────────
  /** Handle pallet creation — expands newly created pallets for visibility */
  handlePalletCreation: (pallets: LocalPallet[]) => void;
  /** Delete pallet */
  deletePallet: (palletId: string) => void;
  /** Assign selected lines to pallet */
  moveSelectedLinesToPallet: (palletId: string) => void;
  /** Unassign selected lines */
  unassignSelectedLines: () => void;
  /** Assign single line to pallet */
  assignLineToPallet: (lineId: string, palletId: string) => void;
  /** Unassign single line */
  unassignLine: (lineId: string) => void;
}

/**
 * Minimalist pallet view hook
 *
 * All business logic is delegated to external functions.
 * This hook only manages state and orchestrates function calls.
 */
export function usePalletView({
  consignmentId,
  consignmentStatus = CONSIGNMENT_STATUS.NR,
  isReadOnly = false
}: {
  consignmentId: string;
  consignmentStatus?: ConsignmentStatusCode;
  isReadOnly?: boolean;
}): UsePalletViewReturn {
  const { projectId, siteId } = useProjectSiteParams();
  // ─── Permissions & API Data ───────────────────────────────────────────────
  const { useConsignmentLinePermissions } = usePermissions();
  const permissions = useConsignmentLinePermissions(false, consignmentStatus);
  const canEdit = permissions.canEditLine && !isReadOnly;

  const { data, isLoading, error } = useGetConsignmentLinesQuery(
    { consignmentId, projectId, siteId },
    { skip: !consignmentId || !projectId || !siteId }
  );

  // ─── API Hooks ────────────────────────────────────────────────────────────
  const { deletePallet: deletePalletApi } = useDeletePallet({
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to delete pallet'))
  });

  const { assignLineToPallet: assignLineToPalletApi } = useAssignLineToPallet({
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to update pallet assignment'))
  });

  // ─── State ─────────────────────────────────────────────────────────────────
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // ─── Computed Values ───────────────────────────────────────────────────────
  const lines = useMemo(() => {
    return data?.items ?? [];
  }, [data]);

  // Pallets come from the lines response `pallets` array (source of truth,
  // including empty pallets and metadata); falls back to line-derived pallets
  // on older backends that don't return the array.
  const pallets = useMemo<LocalPallet[]>(() => buildConsignmentPallets(data?.pallets, lines), [data, lines]);

  const tableData = useMemo(() => transformToHierarchicalRows(lines, pallets), [lines, pallets]);

  const selectedLinesInfo = useMemo(() => calculateSelectedLinesInfo(selectedLineIds, lines), [selectedLineIds, lines]);

  // ─── UI Actions (minimal logic, just state updates) ───────────────────────
  const toggleExpanded = useCallback((palletId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(palletId)) {
        next.delete(palletId);
      } else {
        next.add(palletId);
      }
      return next;
    });
  }, []);

  // ─── Business Actions ──────────────────────────────────────────────────────
  const handlePalletCreation = useCallback((newPallets: LocalPallet[]) => {
    // Pallets (and any line splitting) are created on the backend; the lines
    // query is invalidated by the mutations and refetched automatically.
    // Here we only expand the new pallets for visibility and clear selection.
    setExpandedRows((prev) => {
      const next = new Set(prev);
      newPallets.forEach((p) => next.add(p.id));
      return next;
    });

    setSelectedLineIds([]);
  }, []);

  const deletePallet = useCallback(
    async (palletId: string) => {
      try {
        await deletePalletApi({
          consignmentId,
          palletId,
          projectId,
          siteId
        });

        setExpandedRows((prev) => {
          const next = new Set(prev);
          next.delete(palletId);
          return next;
        });
      } catch {
        // The mutation's onError already surfaced a toast.
      }
    },
    [consignmentId, projectId, siteId, deletePalletApi]
  );

  const moveSelectedLinesToPallet = useCallback(
    async (palletId: string) => {
      // Resolve the pallet to assign by its id
      const pallet = pallets.find((p) => p.id === palletId);
      if (!pallet) return;

      // Capacity guard: count only the lines newly placed on this pallet (lines
      // already assigned to it are part of currentWeight/currentVolume already).
      const addedLines = lines.filter(
        (line) => selectedLineIds.includes(line.id) && !pallet.assignedLineIds.includes(line.id)
      );
      const capacity = checkPalletCapacity(pallet, addedLines);
      if (!capacity.ok) {
        const unit = capacity.dimension === 'weight' ? 'kg' : 'm³';
        toast.error(
          `Not enough capacity on pallet ${pallet.palletNumber}: ${capacity.dimension} ` +
            `${capacity.resulting?.toFixed(2)} ${unit} would exceed the ${capacity.max} ${unit} limit`
        );
        return;
      }

      try {
        // Assign each selected line to the pallet
        await Promise.all(
          selectedLineIds.map((lineId) =>
            assignLineToPalletApi(
              {
                consignmentId,
                lineId,
                projectId,
                siteId
              },
              {
                palletUuid: pallet.id
              }
            )
          )
        );

        setExpandedRows((prev) => new Set([...prev, palletId]));
        setSelectedLineIds([]);
      } catch {
        // The mutation's onError already surfaced a toast; keep the selection so
        // the user can retry or adjust.
      }
    },
    [selectedLineIds, pallets, lines, consignmentId, projectId, siteId, assignLineToPalletApi]
  );

  const unassignSelectedLines = useCallback(async () => {
    try {
      // Unassign each selected line (set palletId to null)
      await Promise.all(
        selectedLineIds.map((lineId) =>
          assignLineToPalletApi(
            {
              consignmentId,
              lineId,
              projectId,
              siteId
            },
            {
              palletUuid: null
            }
          )
        )
      );

      setSelectedLineIds([]);
    } catch {
      // The mutation's onError already surfaced a toast.
    }
  }, [selectedLineIds, consignmentId, projectId, siteId, assignLineToPalletApi]);

  const assignLineToPallet = useCallback(
    async (lineId: string, palletId: string) => {
      const pallet = pallets.find((p) => p.id === palletId);
      if (!pallet) return;

      // Capacity guard (skip if the line is already on this pallet).
      const addedLines = lines.filter((line) => line.id === lineId && !pallet.assignedLineIds.includes(line.id));
      const capacity = checkPalletCapacity(pallet, addedLines);
      if (!capacity.ok) {
        const unit = capacity.dimension === 'weight' ? 'kg' : 'm³';
        toast.error(
          `Not enough capacity on pallet ${pallet.palletNumber}: ${capacity.dimension} ` +
            `${capacity.resulting?.toFixed(2)} ${unit} would exceed the ${capacity.max} ${unit} limit`
        );
        return;
      }

      try {
        await assignLineToPalletApi(
          {
            consignmentId,
            lineId,
            projectId,
            siteId
          },
          {
            palletUuid: pallet.id
          }
        );

        setExpandedRows((prev) => new Set([...prev, palletId]));
      } catch {
        // The mutation's onError already surfaced a toast.
      }
    },
    [pallets, lines, consignmentId, projectId, siteId, assignLineToPalletApi]
  );

  const unassignLine = useCallback(
    async (lineId: string) => {
      try {
        await assignLineToPalletApi(
          {
            consignmentId,
            lineId,
            projectId,
            siteId
          },
          {
            palletUuid: null
          }
        );
      } catch {
        // The mutation's onError already surfaced a toast.
      }
    },
    [consignmentId, projectId, siteId, assignLineToPalletApi]
  );

  return {
    // Data
    tableData,
    pallets,
    isLoading,
    error,
    canEdit,

    // Selection
    selectedLineIds,
    selectedLinesInfo,
    setSelectedLineIds,

    // UI
    expandedRows,
    toggleExpanded,

    // Actions
    handlePalletCreation,
    deletePallet,
    moveSelectedLinesToPallet,
    unassignSelectedLines,
    assignLineToPallet,
    unassignLine
  };
}
